import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchRawMaterialItems,
    createItem,
    updateItem,
    deleteItem,
    setPagination,
    setSort,
    searchItems,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/item/itemActions';
import ItemForm from '../../components/pageModule/Items/ItemForm';

const MySwal = withReactContent(Swal);

const RawMaterialItems = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    selectedIds = [],
    columnKeys,
    columns: columnsOverride,
}) => {

    const itemState = useSelector(state => state.item);
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createItem(formData));
            toast.success('Item created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating item', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateItem(id, formData));
            toast.success('Item updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating item', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteItem(id));
                toast.success('Item deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting item', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const allColumns = useMemo(() => [
        {
            id: 'name',
            name: 'Name',
            selector: row => row.name?.en ?? '-',
            sortable: true,
            sortField: 'name',
        },
        {
            id: 'item_code',
            name: 'Item Code',
            selector: row => row.item_code,
        },
        {
            id: 'category',
            name: 'Category',
            selector: row => row.category?.label ?? '-',
        },
        {
            id: 'item_type',
            name: 'Item Type',
            selector: row => row.item_type?.label ?? '-',
        },
        {
            id: 'vat_group',
            name: 'VAT Group',
            selector: row => row.vat_group?.label ?? '-',
        },
        {
            id: 'unit_price',
            name: 'Unit Price',
            selector: row => row.unit_price,
        },
        {
            id: 'quantity_available',
            name: 'Quantity In Stock',
            selector: row => {
                const qty = parseFloat(row.available_stock) || 0;
                return `${qty.toFixed(3)} ${row.measurement_unit?.label ?? ''}`;
            },
            sortable: true,
            sortField: 'available_stock',
        },
        {
            id: 'unit_cost',
            name: 'Unit Cost',
            selector: row => row.unit_cost,
        },
        {
            id: 'status',
            name: 'Status',
            selector: row => row.is_active ?
                <span className="badge bg-success">Active</span> :
                <span className="badge bg-danger">Inactive</span>,
        },
    ], []);

    const columns = useMemo(() => {
        if (columnsOverride) {
            return columnsOverride;
        }
        if (!columnKeys?.length) {
            return allColumns;
        }
        const keySet = new Set(columnKeys);
        return allColumns.filter(col => keySet.has(col.id));
    }, [allColumns, columnKeys, columnsOverride]);

    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter) => dispatch(fetchRawMaterialItems(queryFilter, activeFilter, shouldUseFilter)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchItems(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    const defaultAvailableActions = ['create', 'search', 'filter', 'actions', 'edit'];
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Items"
                moduleName="Item"
                moduleKey="items"
                state={itemState}
                actions={actions}
                sidebarClassName="right-sidebar-80"
                FormComponent={ItemForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={true}
                onSelectedRowsChange={onSelectedRowsChange}
                selectedIds={selectedIds}
            />
        </>
    );
};

export default RawMaterialItems; 